import { lazy, StrictMode, Suspense, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from './components/ErrorBoundary';
import './styles/project-x-landing.css';

// The original website bootstrap and all scenes remain untouched in main.tsx.
// Keep the control panel out of the public film's initial bundle.
const BotControlPage = lazy(() => import('./components/BotControlPage'));
function ProjectXLanding() {
  const video = useRef<HTMLVideoElement>(null);
  const music = useRef<HTMLAudioElement>(null);
  const musicFade = useRef(0);
  const starting = useRef(false);
  const [phase, setPhase] = useState<'ready' | 'starting' | 'playing' | 'ended' | 'error'>('ready');

  useEffect(() => () => cancelAnimationFrame(musicFade.current), []);

  const resumeMusic = () => {
    void music.current?.play().then(() => {
      if (video.current) video.current.muted = true;
    }).catch(() => {
      // Keep the film audible if the separate soundtrack cannot load.
      if (video.current && !video.current.ended) video.current.muted = false;
    });
  };

  const repeatMusic = () => {
    const track = music.current;
    if (!track) return;
    // Skip the film's wind, portal and title effects on subsequent repeats.
    track.currentTime = 18.5;
    track.volume = 0;
    const start = performance.now();
    cancelAnimationFrame(musicFade.current);
    const fadeIn = (now: number) => {
      track.volume = Math.min(1, (now - start) / 1200);
      if (track.volume < 1) musicFade.current = requestAnimationFrame(fadeIn);
    };
    resumeMusic();
    musicFade.current = requestAnimationFrame(fadeIn);
  };

  const play = () => {
    if (!video.current || starting.current || phase === 'ended') return;
    starting.current = true;
    setPhase('starting');
    // Unlock both media elements in the same click gesture, including on mobile.
    if (music.current) music.current.currentTime = video.current.currentTime;
    resumeMusic();
    video.current.play().catch(() => {
      music.current?.pause();
      starting.current = false;
      setPhase('error');
    });
  };

  return <main className={`project-x-landing is-${phase}`} aria-label="PROJECT X">
    <audio ref={music} src="/farewell/project-x/project-x-v2-continuous.m4a"
      preload="auto" onEnded={repeatMusic} />
    <video ref={video} className="project-x-film" preload="auto" playsInline muted
      disablePictureInPicture disableRemotePlayback controlsList="nodownload noremoteplayback"
      onPlaying={() => {
        setPhase('playing');
        if (music.current && video.current) {
          if (Math.abs(music.current.currentTime - video.current.currentTime) > 0.15) {
            music.current.currentTime = video.current.currentTime;
          }
          resumeMusic();
        }
      }}
      onWaiting={() => music.current?.pause()}
      onPause={() => {
        if (video.current && !video.current.ended) music.current?.pause();
      }}
      onEnded={() => { starting.current = false; setPhase('ended'); }}
      onError={() => { music.current?.pause(); starting.current = false; setPhase('error'); }}>
      <source src="/farewell/project-x/project-x-v2.mp4" type="video/mp4" />
    </video>
    {phase === 'ended' && <div className="project-x-end-card">
      <a className="project-x-invite" href="https://discord.gg/Kf287nfBx2"
        target="_blank" rel="noopener noreferrer"
        aria-label="Join the Project X Discord (opens in a new tab)">discord.gg/Kf287nfBx2</a>
    </div>}
    {(phase === 'ready' || phase === 'error') && <button className="project-x-enter" onClick={play}>
      {phase === 'error' ? 'unable to play — click to retry' : 'click to enter'}
    </button>}
    {phase === 'starting' && <span className="project-x-loading" role="status">loading</span>}
  </main>;
}

createRoot(document.getElementById('root')!).render(<StrictMode>
  {window.location.pathname.replace(/\/$/, '') === '/bot'
    ? <ErrorBoundary><Suspense fallback={<p role="status">Loading controls…</p>}><BotControlPage /></Suspense></ErrorBoundary>
    : <ProjectXLanding />}
</StrictMode>);
